import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { WorkshopAppointment } from '@chancenwerk/shared/workshops/entities/WorkshopAppointment.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { of, switchMap } from 'rxjs';

/**
 * @customElement akademie-component-workshop-appointment-edit
 */
@customElement('akademie-component-workshop-appointment-edit')
export class AkademieComponentWorkshopAppointmentEdit extends ChemistryLitElement {
  @property({ attribute: false }) workshopID = new RXController(this, '');
  @property({ attribute: false }) workshopAppointment = new RXController(
    this,
    new WorkshopAppointment({
      start: DateTime.now().toJSDate(),
      end: DateTime.now().plus({ hours: 1 }).toJSDate(),
    }),
  );

  @state() _form = new RXController(
    this,
    this.workshopAppointment.observable.pipe(
      switchMap(val => {
        return of(
          new FormController(this, val, {
            onSuccess: async (ent: WorkshopAppointment) => {
              try {
                if (ent._id) {
                  await ent.save();
                } else {
                  await ent.create();
                }
              } catch (e: any) {
                this.dispatchEvent(new CustomEvent('error', { detail: e }));
              }
            },
          }),
        );
      }),
    ),
  );

  private _timeInput(type: 'start' | 'end') {
    if (!this._form.value?.document?.[type]) {
      return;
    }

    return html`<cr-flex space="sm">
      <cr-text bold>${type === 'start' ? 'Startzeit' : 'Endzeit'}</cr-text>
      <cr-grid columns="100px 100px" space="sm">
        <cr-dropdown-selection
          placeholder="Stunde"
          .value=${this._form.value?.document?.[type]?.hour}
          .selectables=${Array.from({ length: 24 }, (_, i) => i).map(i => (i + 1).toString())}
          @value-picked=${(e: ValueEvent<string>) => {
            if (!this._form.value?.document?.[type]) {
              return;
            }

            this._form.value.document[type] = this._form.value?.document?.[type]?.set({
              hour: parseInt(e.detail.value),
            });
          }}
        ></cr-dropdown-selection>
        <cr-dropdown-selection
          placeholder="Minute"
          .value=${this._form.value.document?.[type]?.minute}
          .selectables=${new Array(12).fill(0).map((_, i) => (i * 5).toString())}
          @value-picked=${(e: ValueEvent<string>) => {
            if (!this._form.value?.document?.[type]) {
              return;
            }

            this._form.value.document[type] = this._form.value?.document?.[type]?.set({
              minute: parseInt(e.detail.value),
            });
          }}
        >
        </cr-dropdown-selection>
      </cr-grid>
    </cr-flex>`;
  }

  override render() {
    if (!this._form.value) return html``;

    return html` <cr-flex space="md">
      <cr-date-picker-dropdown
        .end-year=${DateTime.now().plus({ years: 5 }).year}
        placeholder="wann?"
        click-to-save
        flex
        clearable
        @value-picked=${(e: ValueEvent<DateTime>) => {
          if (!this._form.value?.document?.start) {
            return;
          }

          this._form.value.document.start = this._form.value?.document?.start?.set({
            year: e.detail.value.year,
            month: e.detail.value.month,
            day: e.detail.value.day,
          });
        }}
      ></cr-date-picker-dropdown>
      <cr-flex horizontal space="lg"> ${this._timeInput('start')} ${this._timeInput('end')} </cr-flex>
      <cr-button icon="save" ${css({ maxWidth: '250px' })} @click=${async () => this._save()}>Speichern</cr-button>
    </cr-flex>`;
  }

  private async _save() {
    if (this._form.value?.document && !this._form.value.document.workshopID) {
      this._form.value.document.workshopID = this.workshopID.value;
    }

    const res = await this._form.value?.validate();
    if (res) {
      this.dispatchEvent(new CustomEvent('saved', { detail: { value: res } }));
    }
  }
}
